/**
 * Variant-specific support section for school pages.
 *
 * What: Renders the "How DeepThink supports [School] students" section with a heading and bullet points.
 * Why: Each school variant (primary, secondary, IP, IB, mixed) needs distinct support messaging.
 * When: Update when variant copy, bullet structure, or styling changes.
 */
import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

/** Anchor + scroll-reveal key; see `sectionRevealMap.support`. */
export const SCHOOL_SUPPORT_SECTION_ID = 'school-support';

interface SchoolSupportSectionProps {
  heading: string;
  bullets: string[];
}

function SchoolSupportSection({ heading, bullets }: SchoolSupportSectionProps) {
  const titleId = 'school-support-title';

  return (
    <LandingSectionShell
      id={SCHOOL_SUPPORT_SECTION_ID}
      ariaLabelledby={titleId}
      // Spacing between these sections comes from the page-level stack.
      mb={0}
      sx={{ width: '100%' }}
    >
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: radii.md,
          backgroundColor: colors.backgroundAlt,
          boxShadow: shadows.card,
          p: { xs: 2.5, sm: 3, md: 4 },
        }}
      >
        <Stack spacing={{ xs: 2.5, sm: 3 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{
              fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.625rem' },
              fontWeight: 800,
              color: colors.ink,
              lineHeight: 1.25,
              letterSpacing: '-0.02em',
            }}
          >
            {heading}
          </Typography>

          <Stack
            component="ul"
            spacing={1.5}
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
            }}
          >
            {bullets.map((bullet, index) => (
              <Stack
                key={index}
                component="li"
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: colors.washAmber,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: '2px',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: colors.accentOrange,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                    color: colors.inkMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {bullet}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Paper>
    </LandingSectionShell>
  );
}

export default SchoolSupportSection;
